import { useMemo, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
  Table,
} from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { ArrowRightOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { FileText } from 'lucide-react';

import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import { useDocuments } from '../hooks/useDocuments.js';
import { useDocumentMutations } from '../hooks/useDocumentMutations.js';
import type { DocumentType, DocumentStatus, CreateDocumentInput } from '../api/documents.client.js';

const { Text } = Typography;

interface DocumentRow {
  id: string;
  title: string;
  type: DocumentType;
  status: DocumentStatus;
  amount: number | string | null;
  currency: string;
  contactName?: string | null;
  dealTitle?: string | null;
  createdAt: string;
}

export function DocumentsPage(): ReactElement {
  const navigate = useNavigate();
  const [form] = Form.useForm<CreateDocumentInput>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all');
  
  const documentsQuery = useDocuments();
  const { createMutation } = useDocumentMutations();

  const visibleDocuments = useMemo(() => {
    return (documentsQuery.documents ?? []).filter((doc) => {
      if (typeFilter !== 'all' && doc.type !== typeFilter) return false;
      if (statusFilter !== 'all' && doc.status !== statusFilter) return false;
      if (activeQuery) {
        const query = activeQuery.toLowerCase();
        if (!doc.title.toLowerCase().includes(query)) return false;
      }
      return true;
    }).map(doc => ({
      ...doc,
      contactName: doc.contact?.firstName ? `${doc.contact.firstName} ${doc.contact.lastName || ''}`.trim() : null,
      dealTitle: doc.deal?.title ?? null,
    }));
  }, [documentsQuery.documents, typeFilter, statusFilter, activeQuery]);

  const pagination = useDataViewPagination(visibleDocuments);

  async function handleCreate(values: CreateDocumentInput): Promise<void> {
    try {
      const created = await createMutation.mutateAsync(values);
      form.resetFields();
      setIsCreateOpen(false);
      navigate(`/documents/${created.id}`);
    } catch {
      // error handled by react-query
    }
  }

  function handleSearch(value: string): void {
    const nextQuery = value.trim();
    setSearchQuery(nextQuery);
    setActiveQuery(nextQuery);
    pagination.resetPage();
  }

  const columns: ColumnsType<DocumentRow> = [
    {
      title: 'Название',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record) => (
        <Link
          to={`/documents/${record.id}`}
          style={{ fontWeight: 500, color: '#44403c' }}
          onClick={(e) => e.stopPropagation()}
        >
          {title}
        </Link>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: DocumentType) => {
        const colors: Record<DocumentType, string> = { contract: 'gold', invoice: 'purple', act: 'cyan' };
        const labels: Record<DocumentType, string> = { contract: 'Договор', invoice: 'Счет', act: 'Акт' };
        return <Tag color={colors[type]}>{labels[type]}</Tag>;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: DocumentStatus) => {
        const colors: Record<DocumentStatus, string> = { draft: 'default', sent: 'blue', signed: 'green', cancelled: 'red' };
        const labels: Record<DocumentStatus, string> = { draft: 'Черновик', sent: 'Отправлен', signed: 'Подписан', cancelled: 'Отменен' };
        return <Tag color={colors[status]}>{labels[status]}</Tag>;
      },
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (amount: string | null) => (
        amount ? (
          <span className="font-mono font-medium text-gray-700">
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(Number(amount))}
          </span>
        ) : <Text disabled>—</Text>
      ),
    },
    {
      title: 'Клиент / Сделка',
      key: 'relation',
      render: (_, record) => (
        <div className="flex flex-col">
          <Text style={{ fontSize: 13 }}>{record.contactName ?? <Text disabled>—</Text>}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.dealTitle ?? ''}</Text>
        </div>
      ),
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'right',
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {new Date(date).toLocaleDateString('ru-RU')}
        </Text>
      ),
    },
    {
      title: 'Действие',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Button
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/documents/${record.id}`);
          }}
        >
          Открыть
        </Button>
      ),
    },
  ];

  return (
    <div className="animate-fade-in flex h-full flex-col gap-6">
      <header className="z-10 flex w-full shrink-0 items-center justify-between">
        <div>
          <h1 className="m-0 mb-1 font-bold leading-tight tracking-tight" style={{ color: '#2d2418', fontSize: 28 }}>
            Документы
          </h1>
          <Text type="secondary" style={{ display: 'block', fontSize: 13, marginTop: 4 }}>
            <span data-testid="documents-visible-count">{visibleDocuments.length}</span> документов
          </Text>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateOpen(true)}
            style={{ borderRadius: 8, height: 36, padding: '0 16px', fontWeight: 500 }}
          >
            Создать документ
          </Button>
          <Tooltip title="Обновить">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void documentsQuery.refetch()}
              style={{ borderRadius: 8, height: 36, width: 36 }}
            />
          </Tooltip>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div
          className="flex w-full shrink-0 items-center gap-4 rounded-2xl border bg-white px-4 py-3 shadow-sm"
          style={{ borderColor: '#e7e5e4' }}
        >
          <div className="flex-1" style={{ maxWidth: 400 }}>
            <Input
              placeholder="Поиск по названию..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setActiveQuery(e.target.value.trim());
                pagination.resetPage();
              }}
              onPressEnter={(e) => handleSearch(e.currentTarget.value)}
              variant="borderless"
              className="transition-colors"
              style={{ backgroundColor: '#f5f5f4', borderRadius: 8, padding: '8px 12px' }}
              allowClear
            />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as DocumentType | 'all');
                pagination.resetPage();
              }}
              className="bg-transparent font-medium transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                color: '#44403c',
                fontSize: 14,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">Все типы</option>
              <option value="contract">Договор</option>
              <option value="invoice">Счет</option>
              <option value="act">Акт</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as DocumentStatus | 'all');
                pagination.resetPage();
              }}
              className="bg-transparent font-medium transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                color: '#44403c',
                fontSize: 14,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">Все статусы</option>
              <option value="draft">Черновик</option>
              <option value="sent">Отправлен</option>
              <option value="signed">Подписан</option>
              <option value="cancelled">Отменен</option>
            </select>
          </div>
        </div>

        <div
          className="min-h-0 flex-1"
          style={{ background: '#fffdfa', border: '1px solid #e7e5e4', borderRadius: 8, overflow: 'hidden' }}
        >
          {documentsQuery.isLoading ? (
            <div className="flex flex-col gap-3 p-6">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} title={false} paragraph={{ rows: 1 }} active />
              ))}
            </div>
          ) : (
            <Table<DocumentRow>
              dataSource={visibleDocuments}
              columns={columns}
              rowKey="id"
              size="small"
              pagination={pagination.pagination}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span style={{ color: '#a8a29e', fontSize: 13 }}>Нет документов.</span>}
                  />
                ),
              }}
              onRow={(record) => ({
                style: { cursor: 'pointer' },
                onClick: () => navigate(`/documents/${record.id}`),
                onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = '#fffbeb'; },
                onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; },
              })}
            />
          )}
        </div>
      </div>

      <Modal
        title="Создать документ"
        open={isCreateOpen}
        onCancel={() => setIsCreateOpen(false)}
        footer={null}
        width={440}
        destroyOnHidden={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void handleCreate(values)}
          requiredMark={false}
          className="mt-4"
          initialValues={{ type: 'contract' }}
        >
          <Form.Item
            name="title"
            label={<label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, color: '#78716c' }}>Название документа</label>}
            rules={[{ required: true, message: 'Название обязательно' }]}
          >
            <Input placeholder="Договор №..." prefix={<FileText size={16} style={{ color: '#d6d3d1' }} />} />
          </Form.Item>

          <Form.Item
            name="type"
            label={<label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, color: '#78716c' }}>Тип документа</label>}
            rules={[{ required: true }]}
          >
            <select
              className="w-full bg-transparent font-medium transition-colors"
              style={{ height: 36, border: '1px solid #e7e5e4', borderRadius: 8, padding: '0 12px', color: '#44403c', fontSize: 14, outline: 'none' }}
            >
              <option value="contract">Договор</option>
              <option value="invoice">Счет</option>
              <option value="act">Акт</option>
            </select>
          </Form.Item>

          <div className="flex justify-end gap-2 mt-6">
            <Button onClick={() => setIsCreateOpen(false)}>Отмена</Button>
            <Button type="primary" htmlType="submit" loading={createMutation.isPending} icon={<PlusOutlined />}>
              Создать
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
